import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { getSports, type PlatformSport } from "../../services/sports";
import { getClubSettings, updateClubSettings, getClubDetails, updateClub } from "../../services/api";
import ClubAdminLayout from "../../layouts/ClubAdminLayout";
import { ClubNavIcon } from "../../layouts/clubNav";
import AdminPageHeader, { AdminButton } from "../../components/admin/AdminPageHeader";
import { SectionCard } from "../../components/admin/AdminCard";
import ClubPaymentsCard from "../../components/club/ClubPaymentsCard";
import ClubDetailsFields, { type ClubDetailsFormState } from "./ClubDetailsForm";
import toast from "react-hot-toast";
import { useFeatures } from "../../hooks/useFeatures";

const EMPTY_DETAILS: ClubDetailsFormState = {
  name: "", description: "", physical_address: "", city: "", county: "", website: "", logo_url: "",
  brand_color: "", brand_color_secondary: "", gallery: [], sports: [],
  legal_name: "", cui: "", registration_number: "", business_address: "", bank_account: "", bank_name: "",
};

export default function ClubSettings() {
  const { t } = useTranslation();
  const { activeClubId, hasClubCapability, refresh } = useAuth();
  const { features } = useFeatures();
  const [notifyAthletes, setNotifyAthletes] = useState(true);
  const [siblingPercent, setSiblingPercent] = useState(0);
  const [siblingMode, setSiblingMode] = useState<"additional" | "all">("additional");
  const canBrand = hasClubCapability("customBranding");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [details, setDetails] = useState<ClubDetailsFormState>(EMPTY_DETAILS);
  const [platformSports, setPlatformSports] = useState<PlatformSport[]>([]);
  const [selectedSports, setSelectedSports] = useState<string[]>([]);
  const [coordinatorsOn, setCoordinatorsOn] = useState(false);

  useEffect(() => {
    if (activeClubId) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeClubId]);

  const loadData = async () => {
    if (!activeClubId) return;
    setLoading(true);
    try {
      const [sports, settings, clubDetails] = await Promise.all([
        getSports(),
        getClubSettings(activeClubId),
        getClubDetails(activeClubId),
      ]);
      setPlatformSports(sports);
      setCoordinatorsOn(!!settings.coordinators_enabled);
      // Defaults to on when the field has not been set yet.
      setNotifyAthletes(settings.reminders_notify_athlete !== false);
      setSiblingPercent(settings.sibling_discount_percent ?? 0);
      setSiblingMode(settings.sibling_discount_mode === "all" ? "all" : "additional");
      // Stored values should be sport ids, but older saves wrote names into the
      // same column. Normalise to ids so those clubs see their real selection
      // (and get it corrected on the next save).
      const byName = new Map(sports.map((s) => [s.name, s.id]));
      const knownIds = new Set(sports.map((s) => s.id));
      setSelectedSports(
        (settings.sports || [])
          .map((v) => (knownIds.has(v) ? v : byName.get(v)))
          .filter((v): v is string => !!v)
      );
      if (clubDetails) {
        setDetails({
          name: clubDetails.name || "",
          description: clubDetails.description || "",
          physical_address: clubDetails.physical_address || "",
          city: clubDetails.city || "",
          county: clubDetails.county || "",
          website: clubDetails.website || "",
          logo_url: clubDetails.logo_url || "",
          brand_color: clubDetails.brand_color || "",
          brand_color_secondary: clubDetails.brand_color_secondary || "",
          gallery: clubDetails.gallery || [],
          sports: clubDetails.sports || [],
          legal_name: clubDetails.legal_name || "",
          cui: clubDetails.cui || "",
          registration_number: clubDetails.registration_number || "",
          business_address: clubDetails.business_address || "",
          bank_account: clubDetails.bank_account || "",
          bank_name: clubDetails.bank_name || "",
        });
      }
    } catch (error) {
      console.error("Failed to load settings:", error);
      toast.error(t("error.loadFailed", "Nu s-au putut încărca setările"));
    }
    setLoading(false);
  };

  const handleSportToggle = (sportId: string) => {
    setSelectedSports((prev) => (prev.includes(sportId) ? prev.filter((id) => id !== sportId) : [...prev, sportId]));
  };

  // Single Save — persists club details AND club settings together.
  const handleSaveAll = async () => {
    if (!activeClubId) return;
    setSaving(true);
    try {
      const payload: any = { ...details };
      if (!canBrand) {
        delete payload.brand_color;
        delete payload.brand_color_secondary;
      }
      // Both endpoints write clubs.sports. Sending the details copy too raced
      // with the Sports section below and usually won its own race, silently
      // reverting whatever the admin had just ticked. The section is the only
      // writer now.
      delete payload.sports;
      await Promise.all([
        updateClub(activeClubId, payload),
        // Age groups are platform-wide now, so the club no longer curates its
        // own list — only its sports are stored here.
        updateClubSettings(activeClubId, {
          sports: selectedSports,
          coordinators_enabled: coordinatorsOn,
          reminders_notify_athlete: notifyAthletes,
          // Only sent while the module is live. The server refuses these
          // fields when discounts are off, and it should refuse them — a club
          // whose rule was accepted would believe families are getting it.
          ...(features.paymentsDiscounts
            ? { sibling_discount_percent: siblingPercent, sibling_discount_mode: siblingMode }
            : {}),
        }),
      ]);
      // The coordinator menu and the walkthrough both read this from the auth
      // payload, so the change has to land there before the page repaints.
      await refresh();
      toast.success(t("common.saved", "Setările au fost salvate"));
    } catch (error: any) {
      console.error("Failed to save settings:", error);
      toast.error(error?.response?.data?.error || t("error.saveFailed", "Nu s-au putut salva setările"));
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <ClubAdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-4 border-indigo-600 rounded-full animate-spin border-t-transparent"></div>
        </div>
      </ClubAdminLayout>
    );
  }

  const SaveButton = (
    <AdminButton onClick={handleSaveAll} disabled={saving}>
      {saving ? (
        <>
          <div className="w-4 h-4 border-2 border-white rounded-full border-t-transparent animate-spin" />
          {t("common.saving", "Se salvează...")}
        </>
      ) : (
        t("common.save", "Salvează")
      )}
    </AdminButton>
  );

  return (
    <ClubAdminLayout>
      <div className="space-y-6 pb-24">
        <AdminPageHeader
          title={t("admin.clubSettings", "Setări Club")}
          subtitle={t("clubSettings.subtitle", "Detaliile clubului, sporturile și grupele de vârstă.")}
          icon={<ClubNavIcon id="settings" className="w-6 h-6" />}
          actions={SaveButton}
        />

        {/* Club details (public + business info, branding, gallery) */}
        <SectionCard title={t("admin.clubDetails", "Detalii club")} bodyPadded>
          <ClubDetailsFields form={details} setForm={setDetails} canBrand={canBrand} />
        </SectionCard>

        {/* Sports — the single place the club picks what it practises. */}
        <SectionCard
          title={t("admin.clubSports", "Sporturi / Activități")}
          subtitle={t("clubSettings.sportsHint", "Sporturile practicate în club — disponibile pentru echipe și evenimente.")}
          bodyPadded
        >
          {platformSports.length === 0 ? (
            <p className="text-sm text-amber-600">
              {t("clubSettings.noPlatformSports", "Nu există sporturi configurate în platformă. Contactează administratorul platformei.")}
            </p>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold tracking-wide uppercase text-slate-400">
                  {selectedSports.length > 0
                    ? t("clubSettings.sportsSelected", "{{count}} selectate", { count: selectedSports.length })
                    : t("clubSettings.sportsNoneSelected", "Niciun sport selectat")}
                </p>
                {selectedSports.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedSports([])}
                    className="text-xs font-semibold transition-colors text-slate-400 hover:text-rose-500"
                  >
                    {t("common.clear", "Golește")}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4">
                {platformSports.map((sport) => {
                  const active = selectedSports.includes(sport.id);
                  return (
                    <button
                      key={sport.id}
                      type="button"
                      onClick={() => handleSportToggle(sport.id)}
                      aria-pressed={active}
                      className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border text-left transition-all ${
                        active
                          ? "border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500/20"
                          : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                      }`}
                    >
                      <span className="text-lg leading-none shrink-0">{sport.icon || "🏅"}</span>
                      <span className={`flex-1 min-w-0 text-sm font-medium truncate ${active ? "text-indigo-900" : "text-slate-700"}`}>
                        {sport.name}
                      </span>
                      {active && (
                        <svg className="w-4 h-4 text-indigo-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </SectionCard>

        {/* Coordinators — off unless the club says otherwise. A club with one
            or two coaches has no use for a layer between them and the admin,
            and an unused role in the menu is just a page that confuses. */}
        <SectionCard
          title={t("admin.coordinators", "Coordonatori")}
          subtitle={t(
            "clubSettings.coordinatorsHint",
            "Pentru cluburile mari: o persoană care răspunde de un singur sport."
          )}
          bodyPadded
        >
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={coordinatorsOn}
              onChange={(e) => setCoordinatorsOn(e.target.checked)}
              className="w-5 h-5 mt-0.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-sm text-slate-600">
              <span className="block font-semibold text-slate-800">
                {t("clubSettings.coordinatorsToggle", "Folosim coordonatori pe sporturi")}
              </span>
              {t(
                "clubSettings.coordinatorsExplain",
                "Coordonatorul vede și gestionează doar echipele, antrenorii și sportivii sportului său și poate invita el însuși antrenori. Cât timp opțiunea e oprită, rolul nu apare nicăieri în aplicație."
              )}
            </span>
          </label>
          {!coordinatorsOn && (
            <p className="mt-3 text-xs text-slate-400">
              {t(
                "clubSettings.coordinatorsOffNote",
                "Dacă ai deja coordonatori, elimină-i întâi din pagina Coordonatori — nimeni nu pierde accesul fără să știi."
              )}
            </p>
          )}
        </SectionCard>

        {/* Collections. Its own card because it is the club's relationship
            with Stripe, not a setting of ours — and it saves itself, so it
            must not sit inside the form's Save. */}
        <ClubPaymentsCard clubId={activeClubId} />

        {features.paymentsReminders && (
        <SectionCard
          title={t("clubSettings.reminders", "Memento-uri de plată")}
          subtitle={t(
            "clubSettings.remindersHint",
            "Părinții primesc memento-uri automate înainte și după scadență. Aici decizi dacă le primește și copilul."
          )}
          bodyPadded
        >
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={notifyAthletes}
              onChange={(e) => setNotifyAthletes(e.target.checked)}
              className="w-5 h-5 mt-0.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-sm text-slate-600">
              <span className="block font-semibold text-slate-800">
                {t("clubSettings.remindersAthlete", "Anunță și sportivul")}
              </span>
              {t(
                "clubSettings.remindersAthleteExplain",
                "Copilul primește doar „amintește-i părintelui”, cu 3 zile înainte și în ziua scadenței — fără sumă și fără cuvântul restant. Părinții primesc memento-urile complete oricum."
              )}
            </span>
          </label>
          {!notifyAthletes && (
            <p className="mt-3 text-xs text-slate-400">
              {t(
                "clubSettings.remindersAthleteOff",
                "Sportivii nu vor primi nicio notificare despre plăți. Părinții, da."
              )}
            </p>
          )}
        </SectionCard>
        )}

        {features.paymentsDiscounts && (
        <SectionCard
          title={t("clubSettings.siblings", "Reducere pentru frați")}
          subtitle={t(
            "clubSettings.siblingsHint",
            "Se aplică automat familiilor cu mai mult de un copil care plătește la club. Nu se cumulează cu bursele și scutirile — acolo decide scutirea."
          )}
          bodyPadded
        >
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block mb-1 text-xs font-semibold text-slate-500">
                {t("clubSettings.siblingsPercent", "Procent reducere")}
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={siblingPercent}
                  onChange={(e) => setSiblingPercent(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                  className="w-24 px-3 py-2 text-base border rounded-lg border-slate-300 lg:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <span className="text-sm text-slate-500">%</span>
              </div>
            </div>
            <div>
              <label className="block mb-1 text-xs font-semibold text-slate-500">
                {t("clubSettings.siblingsMode", "Cui se aplică")}
              </label>
              <select
                value={siblingMode}
                onChange={(e) => setSiblingMode(e.target.value === "all" ? "all" : "additional")}
                disabled={siblingPercent === 0}
                className="px-3 py-2 text-base bg-white border rounded-lg border-slate-300 lg:text-sm disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="additional">
                  {t("clubSettings.siblingsAdditional", "Fraților, în afară de primul înscris")}
                </option>
                <option value="all">{t("clubSettings.siblingsAll", "Tuturor copiilor din familie")}</option>
              </select>
            </div>
          </div>
          <p className="mt-3 text-xs text-slate-500">
            {siblingPercent === 0
              ? t("clubSettings.siblingsOff", "Oprită. Fiecare copil plătește prețul întreg.")
              : siblingMode === "additional"
                ? t(
                    "clubSettings.siblingsAdditionalExplain",
                    "Primul copil înscris plătește întreg, ceilalți cu {{percent}}% mai puțin. „Primul înscris” rămâne același chiar dacă prețurile se schimbă.",
                    { percent: siblingPercent }
                  )
                : t(
                    "clubSettings.siblingsAllExplain",
                    "Fiecare copil al familiei plătește cu {{percent}}% mai puțin, inclusiv primul.",
                    { percent: siblingPercent }
                  )}
          </p>
          <p className="mt-2 text-xs text-slate-400">
            {t(
              "clubSettings.siblingsScope",
              "La salvare, reducerea se aplică perioadelor neachitate. Ce s-a încasat deja rămâne neatins."
            )}
          </p>
        </SectionCard>
        )}

        <div className="flex justify-end">{SaveButton}</div>
      </div>
    </ClubAdminLayout>
  );
}
